/**
 * #37340: the FO-6 roof shapes (farm.barnRoofType codes, sent as the agricultural 360Value OCCUPANCY
 * `rooftype`) that 360Value accepts, by the `use` the request sends.
 *
 * 360Value rejects a shape it does not allow for a use with ERR04 "Primary Use: Roof Type with the option X
 * is incompatible with other questions answered in this use", and the structure gets no RCE. Its request doc
 * lists the shapes but not which uses take them, so this table was measured: one calculate per use × shape
 * against 360Value acceptance on 2026-10-28 (production had rejected the same combinations since #37180 began
 * sending roofShape). Arched/Barrel (B) is accepted by none of these uses, and a poultry house (PCH) accepts
 * no shape at all. Re-measure with the probe in the #37340 PR if 360Value changes.
 *
 * Staff offers only these shapes, and the 360Value request leaves `rooftype` out for any other combination,
 * including a use not in this table: before #37180 no FO-6 call sent a rooftype, and 360Value valued every one.
 */
export const FO6_360VALUE_ROOF_SHAPES_BY_USE: Readonly<Record<string, readonly string[]>> = {
  CB: ['G', 'GM', 'F', 'S'], // Calf Barn
  HS: ['G', 'GM', 'F', 'S'], // Equestrian Barn
  FS: ['G', 'F', 'S'], // Feed Shed / Rest Barn
  LVB: ['G', 'GM', 'GT', 'F', 'S'], // General Use / Livestock Barn
  DB: ['G', 'GM', 'GT', 'F', 'S'], // Dairy Barn
  MS: ['G', 'F', 'S'], // Machine / Equipment Shed
  HB: ['G', 'F', 'S'], // Hay Barn
  Q: ['NONEQ'], // Quonset Building
  3: ['G', 'S'], // any open-sided barn / confinement building (FO6_OPEN_SIDED_360VALUE_USE)
  SBF: ['G', 'F', 'S'], // Confinement: Swine Finishing
  SBFI: ['G', 'F', 'S'],
  SBG: ['G', 'F', 'S'],
  SBN: ['G', 'F', 'S'],
  HP: ['NONEH'], // Hoop Building
  PCH: [] // Poultry Confinement House
};

/** The 360Value use an open-sided barn or confinement building is valued as. */
export const FO6_OPEN_SIDED_360VALUE_USE = '3';

/** The uses that are sent as FO6_OPEN_SIDED_360VALUE_USE when the structure has open sides. */
export const FO6_OPEN_SIDE_ELIGIBLE_USES: readonly string[] = [
  'CB',
  'HS',
  'FS',
  'LVB',
  'DB',
  'MH',
  'MC',
  'GRNA',
  'SBF',
  'SBFI',
  'SBG',
  'SBN',
  'TBD',
  'TBS'
];

/** Hoop Buildings (structure category H) have no occupancy select; 360Value values them as use HP. */
const HOOP_BUILDINGS_CATEGORY = 'H';
const HOOP_BUILDING_360VALUE_USE = 'HP';

type Fo6StructureBuilding = {
  buildingType?: string | null;
  occupancy?: string | null;
  structureOpenSides?: boolean | number | null;
} | null;

/** The `use` the agricultural 360Value request sends for this structure, or null when it isn't known yet. */
export const fo6Structure360ValueUse = (building: Fo6StructureBuilding | undefined): string | null => {
  const use =
    building?.occupancy || (building?.buildingType === HOOP_BUILDINGS_CATEGORY ? HOOP_BUILDING_360VALUE_USE : null);
  if (use && building?.structureOpenSides && FO6_OPEN_SIDE_ELIGIBLE_USES.includes(use)) {
    return FO6_OPEN_SIDED_360VALUE_USE;
  }
  return use;
};

/** The shapes 360Value accepts for the use, or null when the use isn't in the measured table. */
export const fo6RoofShapesAcceptedBy360Value = (use: string | null | undefined): string[] | null => {
  const shapes = use ? FO6_360VALUE_ROOF_SHAPES_BY_USE[use] : undefined;
  return shapes ? [...shapes] : null;
};

/** 360Value accepts the shape for the use. False for a use not in the measured table. */
export const isFo6RoofShapeAcceptedBy360Value = (
  use: string | null | undefined,
  shape: string | null | undefined
): boolean => !!shape && !!fo6RoofShapesAcceptedBy360Value(use)?.includes(shape);
